import React, { useEffect, useState } from "react";

import { useTranslation } from "utils";

import { Button, FieldSelector, Modal, Typography } from "components";

export type FieldReselectionModalProps = {
  open: boolean;
  /** The project's farm — field selection is scoped to it. */
  farmId: string;
  saving: boolean;
  onConfirm: (fieldId: string) => void;
};

/**
 * Blocking modal shown when the active project's connected field is missing or was deleted. The farm is
 * fixed to the project's farm, so no farm picker is shown.
 */
export const FieldReselectionModal = ({ open, farmId, saving, onConfirm }: FieldReselectionModalProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);

  // Reset the (deliberately empty) selection whenever the modal reopens or the farm changes.
  useEffect(() => {
    if (!open) return;
    setSelectedFieldId(null);
  }, [open, farmId]);

  return (
    <Modal
      dataTestId="soilsight-field-reselect-modal"
      className="soilsight-field-reselect-modal"
      width={480}
      showModal={open}
      closable={false}
      keyboard={false}
      maskClosable={false}
      // Below the add-field Drawer's default 1002, so it layers above this modal instead of under it.
      zIndex={1000}
      title={t("fieldReselect.title")}
      content={
        <div className="soilsight-field-reselect-modal__body">
          <Typography variant="paragraph-m" color="grey-600" className="soilsight-field-reselect-modal__description">
            {t("fieldReselect.description")}
          </Typography>
          {/* autoSelectLargest=false: start empty so the user makes a deliberate choice (confirm stays
              disabled until then). autoOpenAddFieldWhenEmpty: open the add-field drawer for empty farms. */}
          <FieldSelector
            farmId={farmId}
            selectedFieldId={selectedFieldId}
            setSelectedFieldId={setSelectedFieldId}
            autoSelectLargest={false}
            autoOpenAddFieldWhenEmpty
          />
        </div>
      }
      footer={
        <Button
          type="primary"
          dataTestId="soilsight-field-reselect-confirm"
          disabled={!selectedFieldId || saving}
          loading={saving}
          onClick={() => selectedFieldId && onConfirm(selectedFieldId)}
        >
          {t("fieldReselect.confirm")}
        </Button>
      }
    />
  );
};

export default FieldReselectionModal;
